/* -------------------------- Design imports start -------------------------- */
import { Card, CardContent } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement } from "react";

import { FilesystemActions } from './FilesystemActions'
import { FilesystemFileview } from './FilesystemFileview'
import { FilesystemNavigation } from './FilesystemNavigation'
import { type FileMetadata, type Folder } from '../utils/types'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  files: FileMetadata[]
  folders: Folder[]
  tickedFiles: string[]
  onFileClick: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, file: string) => void
  onFolderClick: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, folder: string) => void
  onHistoryForwardClick: () => void
  onHistoryBackClick: () => void
  handleBreadcrumbClick: (path: string, folder: string) => void
  history: string
  currentPath: string
  currentDepth: number
  updateTickedStatus: (file: string, type: 'FILE' | 'FOLDER') => void
  getTickedStatus: (file: string) => number
  onTickAllFilesClick: () => void
  onUntickAllFilesClick: () => void
  onShowStlClick: () => void
  onCreateFolderClick: () => void
  onRenameFileClick?: () => void
  onDownloadFileClick: () => void
  onDeleteFileClick: () => void
  allowCreateFolder: boolean
  allowRename: boolean
  allowDownload: boolean
  allowDelete: boolean
  allowShowStl: boolean
  sx?: React.CSSProperties
}
/**
 * Filesystem consisting of:
 *  - FileUpload
 *  - CADUpload
 *  - Filesystem navigation
 *  - Filesystem actions
 *  - Filesystem fileview
 */
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export const Filesystem = (props: Props): ReactElement => {
  /* -------------------------- Non state data start -------------------------- */
  const {
    files,
    folders,
    tickedFiles,
    onFileClick,
    onFolderClick,
    handleBreadcrumbClick,
    onHistoryForwardClick,
    onHistoryBackClick,
    history,
    currentPath,
    currentDepth,
    updateTickedStatus,
    getTickedStatus,
    onTickAllFilesClick,
    onUntickAllFilesClick,
    onShowStlClick,
    onCreateFolderClick,
    onDownloadFileClick,
    onDeleteFileClick,
    allowCreateFolder,
    allowRename,
    allowDownload,
    allowDelete,
    allowShowStl,
    sx,
  } = props
  /* --------------------------- Non state data end --------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <div style={{ ...sx, marginBottom: '50px' }}>
      <Card variant="outlined">
        <CardContent>
          <FilesystemNavigation
            history={history}
            currentDepth={currentDepth}
            currentPath={currentPath}
            handleBreadcrumbClick={handleBreadcrumbClick}
            onHistoryBackClick={onHistoryBackClick}
            onHistoryForwardClick={onHistoryForwardClick}
          />
          <div style={{ display: 'flex', justifyContent: 'end' }}>
            <FilesystemActions
              spacing={1}
              wrapAlign="right"
              onShowStlClick={onShowStlClick}
              onCreateFolderClick={onCreateFolderClick}
              allowCreateFolder={allowCreateFolder}
              /*onRenameFileClick={onRenameFileClick}*/
              onDownloadFileClick={onDownloadFileClick}
              onDeleteFileClick={onDeleteFileClick}
              allowRename={allowRename}
              allowDownload={allowDownload}
              allowDelete={allowDelete}
              allowShowStl={allowShowStl}
              sx={{ marginTop: '8px', marginBottom: '4px' }}
            />
          </div>
          <FilesystemFileview
            files={files}
            folders={folders}
            tickedFiles={tickedFiles}
            onFileClick={onFileClick}
            onFolderClick={onFolderClick}
            updateTickedStatus={updateTickedStatus}
            getTickedStatus={getTickedStatus}
            onTickAllFilesClick={onTickAllFilesClick}
            onUntickAllFilesClick={onUntickAllFilesClick}
          />
        </CardContent>
      </Card>
    </div>
  )
}
